import { type CaseDto, type CaseProcessDto, PROCESS_EVENT_TYPES } from '@bqcrm/shared';
import { Card, Stack, Text, Timeline, Title } from '@mantine/core';
import { IconListCheck } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { ProcessTaskList } from '../../components/process/ProcessTasks';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { formatDateTime } from '../../format';

/** Zakładka „Zadania”: zadania procesowe sprawy (otwarte i zakończone) i historia przejęć, przekazań, zakończeń. */
export function ProcessTab({ c }: { c: CaseDto }) {
  const q = useQuery({ queryKey: ['case-process', c.id], queryFn: () => api<CaseProcessDto>(`/cases/${c.id}/process`) });
  const open = q.data?.tasks.filter((t) => t.status === 'otwarte') ?? [];
  const done = q.data?.tasks.filter((t) => t.status !== 'otwarte') ?? [];
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Stack gap="lg">
        <Stack gap="xs">
          <Title order={5}>Otwarte zadania</Title>
          {open.length ? (
            <ProcessTaskList tasks={open} />
          ) : (
            <Card withBorder>
              <EmptyState
                compact
                icon={<IconListCheck size={20} />}
                title={c.closedAt ? 'Sprawa zamknięta' : 'Brak otwartych zadań'}
                hint={c.closedAt ? undefined : 'Zadania pojawiają się same, gdy sprawa dojdzie do kolejnego kroku (np. po akceptacji oferty — przygotowanie umowy).'}
              />
            </Card>
          )}
        </Stack>
        {done.length > 0 && (
          <Stack gap="xs">
            <Title order={5}>Zakończone</Title>
            <ProcessTaskList tasks={done} />
          </Stack>
        )}
      </Stack>
    </QueryState>
  );
}

/** Historia zadań sprawy: utworzenia, przejęcia, przekazania, zwroty, przesunięcia, zakończenia. */
export function ProcessHistory({ caseId }: { caseId: string }) {
  const q = useQuery({ queryKey: ['case-process', caseId], queryFn: () => api<CaseProcessDto>(`/cases/${caseId}/process`) });
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data?.events.length ? (
        <Timeline bulletSize={14} lineWidth={2}>
          {q.data.events.slice(0, 200).map((e) => (
            <Timeline.Item key={e.id} title={<Text size="sm">{e.taskTitle}</Text>}>
              <Text size="xs">
                {PROCESS_EVENT_TYPES[e.type]}
                {e.toUserName && (e.type === 'przekazanie' || e.type === 'przydzial' || e.type === 'utworzenie') ? `: ${e.toUserName}` : ''}
                {e.comment ? ` — „${e.comment}”` : ''}
              </Text>
              <Text size="xs" c="dimmed">
                {formatDateTime(e.at)}
                {e.userName ? ` · ${e.userName}` : e.type === 'zakonczenie' || e.type === 'utworzenie' ? ' · automatycznie' : ''}
              </Text>
            </Timeline.Item>
          ))}
        </Timeline>
      ) : (
        <EmptyState compact icon={<IconListCheck size={20} />} title="Brak historii zadań" />
      )}
    </QueryState>
  );
}
